/**
 * ADR-136 D2 — turn validator output into findings attached to the nodes they
 * concern.
 *
 * The specimen this exists for: an operator asked "is this workflow valid as
 * written?" and got eight paragraphs of prose about a graph that was on screen.
 * The findings were ALREADY node-scoped — `get_ticket`, `tag_matt`,
 * `build_mention_comment` — and arrived as paragraphs anyway, because there was
 * nowhere structural to put them.
 *
 * No validator change is needed to fix that. Task-scoped errors are already
 * emitted with a `task '<name>':` prefix (validate-activities.py builds
 * `task_label = f"task '{task_name}'"`), so the attribution is in the string;
 * nothing was reading it.
 */

/** How much an operator should care, in the order they should care. */
export type FindingSeverity =
  /** The propose gate will refuse this. Nothing ships until it is fixed. */
  | 'blocks'
  /** Valid, and will bite you later — a nondeterministic step, a typed-in id. */
  | 'warns'
  /** Worth knowing, safe to ignore. */
  | 'info'

export interface Finding {
  /** The task this concerns, or undefined for an activity-wide finding. */
  nodeId?: string
  severity: FindingSeverity
  /** What the operator reads. Already stripped of the `task 'x':` prefix, since
   *  the node it is attached to says which task it is. */
  message: string
  /** The validator's original line, kept for the raw view and for support. */
  raw: string
  /** ADR-136 D5 — the button's text, when a fix is offered. */
  fixLabel?: string
  /** ADR-136 D5 — what gets asked of the assistant when the operator applies it.
   *  A prompt rather than a transform because converting a step needs code
   *  written; a stub would be a change the operator cannot evaluate and the
   *  validator would accept. */
  fixPrompt?: string
}

/**
 * `task 'name':` or `task 'name'.switch[0]:` — the prefix validate-activities.py
 * puts on anything it can attribute to one task. The nested form matters: a
 * switch-clause error belongs on the switch node, not nowhere.
 */
const TASK_PREFIX = /^task\s+'([^']+)'(?:[.[][^:]*)?:\s*/

/**
 * Errors the validator states about the whole activity rather than a task —
 * a missing `name:`, a bad `version:`, the cross-file graph. These have no node
 * and must NOT be dropped: a finding with nowhere to attach is exactly the kind
 * that goes unseen, which is why D2 keeps a prose channel for them.
 */
export function isActivityScoped(f: Finding): boolean {
  return f.nodeId === undefined
}

/**
 * The activity-scoped findings, worst first — what the graph's activity-level
 * badge counts and lists.
 *
 * D2's mitigation for "findings-on-nodes buries whole-activity findings" has two
 * halves: a prose channel, and a badge ON THE GRAPH. Only the first shipped, and
 * the prose channel lives in the right-hand pane's Validation section — which is
 * the correct place for it and the wrong place to LEARN about it, because the
 * operator's eyes are on the canvas. A finding with nowhere to attach was still
 * the one most likely to go unseen; it had simply moved from "not rendered" to
 * "rendered where nobody was looking".
 */
export function activityFindings(findings: Finding[]): Finding[] {
  return findings.filter(isActivityScoped).sort(bySeverity)
}

/**
 * severityFor classifies a validator line.
 *
 * Everything the canonical validator emits BLOCKS — it is the same check the
 * propose gate runs, so if it speaks, the change will be refused. `warns` is
 * reserved for findings from other producers (step-type rules, reference
 * matching) which are added in later phases; encoding the distinction now keeps
 * the UI from having to learn it twice.
 */
export function severityFor(_raw: string): FindingSeverity {
  return 'blocks'
}

/**
 * deriveFindings maps validator errors onto graph nodes.
 *
 * `knownNodeIds` is required and load-bearing: a parsed task name that is not in
 * the graph must fall back to activity scope rather than attach to a node that
 * does not exist, which would render a badge nobody can click and hide the
 * finding behind it.
 */
export function deriveFindings(errors: string[], knownNodeIds: Iterable<string>): Finding[] {
  const known = new Set(knownNodeIds)
  return errors.map(raw => {
    const m = TASK_PREFIX.exec(raw)
    if (m && known.has(m[1])) {
      return {
        nodeId: m[1],
        severity: severityFor(raw),
        message: raw.slice(m[0].length).trim(),
        raw,
      }
    }
    return { severity: severityFor(raw), message: raw.trim(), raw }
  })
}

/** Findings for one node, in severity order. */
export function findingsForNode(findings: Finding[], nodeId: string): Finding[] {
  return findings.filter(f => f.nodeId === nodeId).sort(bySeverity)
}

/** The worst severity on a node, or null when it is clean — what the badge shows. */
export function worstSeverity(findings: Finding[], nodeId: string): FindingSeverity | null {
  const mine = findingsForNode(findings, nodeId)
  return mine.length === 0 ? null : mine[0].severity
}

const ORDER: Record<FindingSeverity, number> = { blocks: 0, warns: 1, info: 2 }

export function bySeverity(a: Finding, b: Finding): number {
  return ORDER[a.severity] - ORDER[b.severity]
}

/**
 * A count per severity, for the summary line.
 *
 * Deliberately not "N problems": "2 will block this from shipping" and "3 could
 * be better" are different sentences, and the specimen's failure was mixing them
 * so the operator could not tell what to do first.
 */
export function summarize(findings: Finding[]): Record<FindingSeverity, number> {
  const out: Record<FindingSeverity, number> = { blocks: 0, warns: 0, info: 0 }
  for (const f of findings) out[f.severity]++
  return out
}
